import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'

/** Único requisito de domínio: o item precisa de um id numérico estável. */
export interface AttackQueueItem {
  id: number
}

export interface UseAttackQueueOptions<T extends AttackQueueItem> {
  /** Lista corrente da tela. Reativa: um refresh pode remover itens da fila. */
  items: Ref<T[]>
  /** Entrar no item (ex.: marcar "em tratativa"). Rejeitar = não dá para entrar. */
  onEnter?: (item: T) => void | Promise<void>
  /** Sair do item, por qualquer motivo (tratado, pulado, saiu da fila). */
  onLeave?: (item: T) => void | Promise<void>
  onDone?: (item: T) => void | Promise<void>
  onSkip?: (item: T) => void | Promise<void>
  /** `onEnter` rejeitou — tipicamente 409 de marca de outra pessoa. */
  onEnterError?: (item: T, error: unknown) => void
  onFinish?: (summary: { done: number; skipped: number }) => void
}

export interface UseAttackQueueReturn<T extends AttackQueueItem> {
  isActive: Ref<boolean>
  isFinished: Ref<boolean>
  /** Uma transição está em andamento. A tela usa para desabilitar os botões. */
  isBusy: Ref<boolean>
  ids: Ref<number[]>
  current: ComputedRef<T | null>
  next: ComputedRef<T | null>
  position: ComputedRef<number>
  total: ComputedRef<number>
  doneCount: Ref<number>
  skippedCount: Ref<number>
  start: (ids: number[]) => Promise<void>
  markDone: () => Promise<void>
  skip: () => Promise<void>
  stop: () => Promise<void>
}

/**
 * Ataque em sequência: um item por vez, na ordem que o servidor definiu.
 *
 * A fila guarda IDS, não linhas: a listagem é recarregada periodicamente e as
 * referências de objeto morrem em todo refresh. Um id que some da listagem é
 * simplesmente pulado — é o caso do item que alguém resolveu enquanto a fila
 * andava, e ele não pode travar o ataque nem virar um cartão fantasma.
 *
 * Duas invariantes seguram o estado nas bordas:
 *
 * - **Uma saída por entrada.** Todo `onEnter` bem-sucedido tem exatamente um
 *   `onLeave` — inclusive quando o item some da listagem, quando a fila é
 *   reiniciada e quando o `stop()` chega antes de o `onEnter` responder. Sem
 *   isso sobra marca de entrada presa no servidor.
 * - **Só a geração corrente escreve.** `start()` e `stop()` invalidam o que
 *   estiver em voo; um callback que responde depois não mexe mais em contador,
 *   índice nem finalização.
 */
export function useAttackQueue<T extends AttackQueueItem>(
  options: UseAttackQueueOptions<T>
): UseAttackQueueReturn<T> {
  const ids = ref<number[]>([])
  const index = ref(0)
  const isActive = ref(false)
  const isFinished = ref(false)
  const isBusy = ref(false)
  const doneCount = ref(0)
  const skippedCount = ref(0)

  /**
   * Item em que a fila entrou e ainda não soltou. É o que permite soltar a
   * entrada mesmo depois que o item sai da listagem — aí não há mais referência
   * viva para passar ao `onLeave`, só esta.
   */
  let enteredItem: T | null = null

  /** Sobe a cada `start`/`stop`: o que estava em voo perde o direito de escrever. */
  let generation = 0

  const isRunning = (run: number): boolean => run === generation

  const findById = (id: number): T | null =>
    options.items.value.find((item) => item.id === id) ?? null

  const itemAt = (position: number): T | null => {
    const id = ids.value[position]
    return id === undefined ? null : findById(id)
  }

  const current = computed<T | null>(() => (isActive.value ? itemAt(index.value) : null))

  const next = computed<T | null>(() => {
    if (!isActive.value) return null
    for (let i = index.value + 1; i < ids.value.length; i += 1) {
      const candidate = itemAt(i)
      if (candidate) return candidate
    }
    return null
  })

  const position = computed(() => (isActive.value ? index.value + 1 : 0))
  const total = computed(() => ids.value.length)

  /** Referência viva do item em que a fila entrou; a guardada, se ele já saiu da listagem. */
  function takeEnteredItem(): T | null {
    const entered = enteredItem
    if (!entered) return null
    enteredItem = null
    return findById(entered.id) ?? entered
  }

  /**
   * Fim da fila. `isActive` CONTINUA verdadeiro de propósito: o painel precisa
   * seguir montado para mostrar o resumo ("6 tratados · 2 pulados") e o botão
   * de voltar. Quem encerra de verdade é o `stop()`.
   */
  function finish(): void {
    isFinished.value = true
    options.onFinish?.({ done: doneCount.value, skipped: skippedCount.value })
  }

  function resetState(): void {
    ids.value = []
    index.value = 0
    doneCount.value = 0
    skippedCount.value = 0
    isActive.value = false
    isFinished.value = false
  }

  /**
   * Avança até um item em que dê para entrar. Ids que sumiram da listagem são
   * descartados em silêncio; `onEnter` que rejeita conta como pulado e avisa.
   */
  async function settleOnPlayableItem(run: number): Promise<void> {
    while (isRunning(run) && index.value < ids.value.length) {
      const item = itemAt(index.value)
      if (!item) {
        index.value += 1
        continue
      }
      if (!options.onEnter) {
        enteredItem = item
        return
      }
      try {
        await options.onEnter(item)
        if (!isRunning(run)) {
          // A fila foi encerrada ou reiniciada enquanto entrávamos: solta o
          // item agora, senão a entrada fica presa sem ninguém para soltá-la.
          await options.onLeave?.(item)
          return
        }
        enteredItem = item
        return
      } catch (error) {
        if (!isRunning(run)) return
        options.onEnterError?.(item, error)
        skippedCount.value += 1
        index.value += 1
      }
    }
    if (!isRunning(run)) return
    finish()
  }

  async function advance(run: number): Promise<void> {
    index.value += 1
    await settleOnPlayableItem(run)
  }

  /**
   * O item corrente sumiu da listagem entre o refresh e o clique: não há o que
   * tratar nem o que pular. Solta a entrada e reassenta no próximo disponível,
   * sem contar nada — do contrário a fila trava num item que não existe mais.
   */
  async function recoverFromMissingCurrent(run: number): Promise<void> {
    if (!isActive.value || isFinished.value) return
    const abandoned = takeEnteredItem()
    if (abandoned) await options.onLeave?.(abandoned)
    if (!isRunning(run)) return
    await settleOnPlayableItem(run)
  }

  async function start(nextIds: number[]): Promise<void> {
    generation += 1
    const run = generation
    isBusy.value = true
    try {
      // Recomeçar com a fila ativa solta o item corrente antes de tudo.
      const previous = takeEnteredItem()
      if (previous) await options.onLeave?.(previous)
      if (!isRunning(run)) return

      if (!nextIds.length) {
        resetState()
        return
      }
      resetState()
      ids.value = [...nextIds]
      isActive.value = true
      await settleOnPlayableItem(run)
    } finally {
      if (isRunning(run)) isBusy.value = false
    }
  }

  async function markDone(): Promise<void> {
    if (isBusy.value) return
    const run = generation
    isBusy.value = true
    try {
      const item = current.value
      if (!item) {
        await recoverFromMissingCurrent(run)
        return
      }
      await options.onDone?.(item)
      if (!isRunning(run)) return
      enteredItem = null
      await options.onLeave?.(item)
      if (!isRunning(run)) return
      doneCount.value += 1
      await advance(run)
    } finally {
      if (isRunning(run)) isBusy.value = false
    }
  }

  async function skip(): Promise<void> {
    if (isBusy.value) return
    const run = generation
    isBusy.value = true
    try {
      const item = current.value
      if (!item) {
        await recoverFromMissingCurrent(run)
        return
      }
      await options.onSkip?.(item)
      if (!isRunning(run)) return
      enteredItem = null
      await options.onLeave?.(item)
      if (!isRunning(run)) return
      skippedCount.value += 1
      await advance(run)
    } finally {
      if (isRunning(run)) isBusy.value = false
    }
  }

  /**
   * Encerra a fila. Nunca é barrado pelo `isBusy`: é a saída de emergência, e o
   * estado fica limpo na hora — quem estiver em voo já perdeu a geração e, se
   * chegar a entrar, solta o item sozinho.
   */
  async function stop(): Promise<void> {
    generation += 1
    const item = takeEnteredItem()
    resetState()
    isBusy.value = false
    if (item) await options.onLeave?.(item)
  }

  return {
    isActive,
    isFinished,
    isBusy,
    ids,
    current,
    next,
    position,
    total,
    doneCount,
    skippedCount,
    start,
    markDone,
    skip,
    stop
  }
}
